import * as R from 'ramda';
import { IntlShape } from 'react-intl';

import {
  Category,
  CategoryCreatePayload,
  CategoryUpdatePayload,
  ItemCreatePayload,
  OptionBindingAttrs,
  Option,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { pickOptionAttrs, pickOptionValueAttrs, pickItemAttrs } from 'apis/restaurant-api/merchant-admin/menu2/utils';

import { Translation } from './../../../apis/restaurant-api/merchant-admin/menu2/types';
import { CreateCategoryFormData, UpdateCategoryFormData } from './types';
import { ExportedItem, ExportedOption, ExportItemsData } from '../types';
import { mergeTranslations } from '../utils';

export const buildUpdateCategoryFormData = (primaryLanguage: Language, category: Category): UpdateCategoryFormData => {
  const nameTranslation = category.name.find(t => t.lang === primaryLanguage);
  const descriptionTranslation = category.description.find(t => t.lang === primaryLanguage);
  return {
    name: nameTranslation ? nameTranslation.value : '',
    description: descriptionTranslation ? descriptionTranslation.value : '',
    image: category.image
      ? { type: 'existing-image', imageUrl: category.image, downloadUrl: `${category.image}/download` }
      : { type: 'no-image' },
  };
};

const createCategoryFormDataToCategoryAttrs = (language: Language, formData: CreateCategoryFormData[number]) => {
  return {
    name: formData.name ? [{ lang: language, value: formData.name, verified: true }] : [], // TODO: Trimming logic (should be on component level)
    description: formData.description ? [{ lang: language, value: formData.description, verified: true }] : [], // TODO: Trimming logic (should be on component level)
    child_categories: formData.childCategories?.map(childCategory => ({
      name: childCategory.name ? [{ lang: language, value: childCategory.name, verified: true }] : [],
    })),
  };
};

export const buildCategoryCreateData = (
  primaryLanguage: Language,
  formData: CreateCategoryFormData[number],
  imageUrl: string | null,
): CategoryCreatePayload => {
  return {
    ...createCategoryFormDataToCategoryAttrs(primaryLanguage, formData),
    item_bindings: [],
    image: imageUrl,
  };
};

const updateCategoryFormDataToCategoryAttrs = (language: Language, formData: UpdateCategoryFormData) => {
  return {
    name: formData.name ? [{ lang: language, value: formData.name, verified: true }] : [], // TODO: Trimming logic (should be on component level)
    description: formData.description ? [{ lang: language, value: formData.description, verified: true }] : [], // TODO: Trimming logic (should be on component level)
  };
};

export const buildCategoryUpdateData = (
  primaryLanguage: Language,
  category: Category,
  formData: UpdateCategoryFormData,
  imageUrl: string | null,
): CategoryUpdatePayload => {
  const attrs = updateCategoryFormDataToCategoryAttrs(primaryLanguage, formData);
  attrs.name = mergeTranslations(attrs.name[0] || null, category.name);
  attrs.description = mergeTranslations(attrs.description[0] || null, category.description);

  return R.clone({
    ...attrs,
    item_bindings: category.item_bindings,
    id: category.id,
    image: imageUrl,
  });
};

const isValidImportData = (data: any): data is ExportItemsData => {
  const isOldMenuModel = !!(data && data.optionInstances);
  return !isOldMenuModel && !!(data && data.options && data.items);
};

export const readAndParseImportFile = (file: File, intl: IntlShape): Promise<ExportItemsData> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result ? reader.result.toString() : '';
      let parsedText;
      try {
        parsedText = JSON.parse(result);
      } catch (error) {
        reject(intl.formatMessage({ id: 'menu.importItemsErrorJsonParsing' }));
      }
      if (!isValidImportData(parsedText)) {
        reject(intl.formatMessage({ id: 'menu.importItemsErrorNotCategoryFile' }));
      }
      resolve(parsedText);
    };
    reader.onerror = () => {
      reject(intl.formatMessage({ id: 'menu.importItemsErrorReadingFile' }));
    };
    reader.readAsText(file);
  });
};

const toComparableOption = (option: Option | ExportedOption) => {
  const defaultValueIndex = option.values.findIndex(optionValue => optionValue.id === option.default_value);
  if (defaultValueIndex < 0) {
    throw new Error("Option's `default_value` is referring to a non-existing option value");
  }
  return {
    ...pickOptionAttrs({ ...option, menu_id: '' }),
    default_value_index: defaultValueIndex,
    values: option.values.map(optionValue => {
      const { enabled, ...comparableOptionValue } = pickOptionValueAttrs(optionValue);
      return comparableOptionValue;
    }),
  };
};

const serializeImportItemOptions = (
  options: Option[],
  json: ExportItemsData,
  item: ExportedItem,
): (OptionBindingAttrs | undefined)[] => {
  return item.option_bindings.map(optionBinding => {
    const option = json.options.find(o => {
      return o.id === optionBinding.option_id;
    });
    if (option === undefined) {
      return;
    }
    const equivalentOption = options.find(o => {
      return R.equals(toComparableOption(o), toComparableOption(option));
    });
    if (equivalentOption === undefined) {
      return;
    }
    return {
      ...optionBinding,
      id: undefined,
      prerequisite_values: [],
      option_id: equivalentOption.id,
    };
  });
};

export const serializeImportItems = (menuId: string, options: Option[], json: ExportItemsData): ItemCreatePayload[] =>
  json.items.map(item => {
    const serializedOptions = serializeImportItemOptions(options, json, item);
    const hasUnknownOptions = serializedOptions.some(option => option === undefined);

    const attrs = pickItemAttrs({ ...item, menu_id: menuId });

    return {
      ...attrs,
      enabled: false,
      name: hasUnknownOptions ? attrs.name.map(name => ({ ...name, value: `* ${name.value}` })) : attrs.name,
      option_bindings: serializedOptions.filter((o: OptionBindingAttrs | undefined): o is OptionBindingAttrs => !!o),
    };
  });

export const getItemNamesWithUnknownOptions = (options: Option[], json: ExportItemsData): Translation[][] =>
  json.items
    .filter(item => {
      const serializedOptions = serializeImportItemOptions(options, json, item);
      return serializedOptions.some(option => option === undefined);
    })
    .map(item => item.name);
